import type { ReactElement } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { Modal, SmartSearch } from '../../components';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Videographer {
  id: string;
  name: string;
  email: string;
  events: number;
}

// TODO: mock data — there's no backend to list videographers yet.
const VIDEOGRAPHERS: Videographer[] = [
  { id: '1', name: 'Alex Rivera', email: 'alex.rivera@example.com', events: 128 },
  { id: '2', name: 'Jordan Lee', email: 'jordan.lee@example.com', events: 74 },
  { id: '3', name: 'Sam Okafor', email: 'sam.okafor@example.com', events: 43 },
  { id: '4', name: 'Priya Nair', email: 'priya.nair@example.com', events: 210 },
  { id: '5', name: 'Diego Fuentes', email: 'diego.fuentes@example.com', events: 12 }
];

const RowButton = styled.button<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  width: 100%;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 11px 13px;
  cursor: pointer;
  transition: background var(--transition);

  &:hover {
    background: rgba(255, 255, 255, 0.05) !important;
  }

  .check {
    width: 19px;
    height: 19px;
    flex-shrink: 0;
    border-radius: 6px;
    display: grid;
    place-items: center;
    transition: all var(--transition);
    border: 1.5px solid
      ${({ $checked }): string => ($checked ? 'rgb(78, 95, 253)' : 'rgba(255, 255, 255, 0.25)')};
    background: ${({ $checked }): string => ($checked ? 'rgb(78, 95, 253)' : 'transparent')};
    color: ${({ $checked }): string => ($checked ? 'rgb(255, 255, 255)' : 'transparent')};
  }

  .check svg {
    display: block;
  }

  .avatar {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    font: 700 12.5px / 1 'Inter', sans-serif;
  }

  .meta {
    min-width: 0;
  }

  .name {
    font-weight: 700;
    font-size: 13px;
    color: rgb(240, 242, 245);
  }

  .sub {
    margin-top: 3px;
    font-size: 12px;
    color: rgba(176, 186, 209, 0.6);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EmptyRow = styled.div`
  padding: 18px 4px;
  text-align: center;
  font-size: 13px;
  font-style: italic;
  color: rgba(176, 186, 209, 0.4);
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  .count {
    font-weight: 500;
    font-size: 12px;
    color: rgba(176, 186, 209, 0.62);
  }

  .actions {
    display: flex;
    gap: 10px;
  }
`;

const FooterButton = styled.button`
  border-radius: 9px;
  font-weight: 600;
  font-size: 13px;
  padding: 9px 17px;
  cursor: pointer;
  transition: opacity 0.15s, background 0.15s;

  &:hover,
  &:focus {
    outline: none;
    opacity: 0.92;
  }
`;

const CheckIcon = (): ReactElement => (
  <svg viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='currentColor' strokeWidth={3}>
    <path d='M5 13l4 4L19 7' />
  </svg>
);

const initials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

export const GrantAccessModal = ({ open, onClose }: Props): ReactElement => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const query = search.trim().toLowerCase();

  const filtered = VIDEOGRAPHERS.filter((videographer) =>
    `${videographer.name} ${videographer.email}`.toLowerCase().includes(query)
  );

  const toggle = (id: string): void => {
    setSelected((previous) => {
      const next = new Set(previous);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const count = selected.size;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Add videographers'
      headerExtra={
        <SmartSearch
          search={search}
          onSearch={setSearch}
          placeholder='Search or type an email to add'
        />
      }
      footer={
        <FooterRow>
          <span className='count'>
            {count} selected
          </span>
          <div className='actions'>
            <FooterButton className='cc-neutral' type='button' onClick={onClose}>
              Cancel
            </FooterButton>
            {/* TODO: no backend to grant access yet — closes for now. */}
            <FooterButton className='cc-primary' type='button' onClick={onClose}>
              Grant Access
            </FooterButton>
          </div>
        </FooterRow>
      }
    >
      {filtered.length > 0 ? (
        filtered.map((videographer) => (
          <RowButton
            key={videographer.id}
            type='button'
            $checked={selected.has(videographer.id)}
            onClick={(): void => {
              toggle(videographer.id);
            }}
          >
            <span className='check'>
              <CheckIcon />
            </span>
            <span className='avatar cc-green'>{initials(videographer.name)}</span>
            <div className='meta'>
              <div className='name'>{videographer.name}</div>
              <div className='sub'>
                {videographer.email} &middot; {videographer.events} events
              </div>
            </div>
          </RowButton>
        ))
      ) : (
        <EmptyRow>No videographers found.</EmptyRow>
      )}
    </Modal>
  );
};
